/* ==========================================================================
   Sharky — unbox and background drift (2026-11)
   Decoration only. The page reads and works exactly the same without this file.

   1. Ribbon unwrap: the product photos (choose cards, look, gift, night) open like a wrapped present
      the first time they scroll into view.
   2. Hero gift box (wide screens): the lid lifts after the box has hopped, with light and stars.
   3. Drifting light: slow glow and twinkles behind each Christmas section; the sections that are
      on screen cross-fade into each other while you scroll.

   Rules this file keeps:
   - Reduced motion: nothing is added (and everything is removed if the setting changes later).
   - Theme editor: nothing is added, so merchants edit what they see.
   - Nothing changes layout: every piece is absolutely positioned, clipped and pointer-events: none.
   - Nothing runs off screen: the drifting light only plays while its section is visible.
   ========================================================================== */

(() => {
  'use strict';

  const doc = document;
  const root = doc.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  const auraEnabled = Boolean(doc.querySelector('meta[name="sku-aura"]'));
  const SVGNS = 'http://www.w3.org/2000/svg';

  const added = new Set();
  const cleanups = [];
  let running = false;

  const make = (tag, className, parent) => {
    const el = doc.createElement(tag);
    if (className) el.className = className;
    el.setAttribute('aria-hidden', 'true');
    parent.append(el);
    added.add(el);
    return el;
  };

  const after = (ms, fn) => {
    const id = window.setTimeout(fn, ms);
    cleanups.push(() => window.clearTimeout(id));
    return id;
  };

  const positioned = (el) => window.getComputedStyle(el).position !== 'static';

  /* --- 1. Drifting light ------------------------------------------------ */
  const SCENES = [
    ['.sc-hero', 'hero'],
    ['.sc-choose', 'choose'],
    ['.scb', 'combo'],
    ['.sc-look', 'look'],
    ['.sc-gift', 'gift'],
    ['.sc-night', 'night'],
    ['.sc-follow', 'follow'],
    ['.sk-voices', 'voices'],
    ['.sc-trust', 'trust'],
  ];

  // Same sparkle positions on every visit: a small seeded generator instead of Math.random.
  const seeded = (text) => {
    let seed = 0;
    for (let i = 0; i < text.length; i += 1) seed = (seed * 31 + text.charCodeAt(i)) >>> 0;
    return () => {
      seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
      return seed / 4294967296;
    };
  };

  const initAura = () => {
    if (!auraEnabled || !('IntersectionObserver' in window)) return;

    const live = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) entry.target.classList.toggle('is-live', entry.isIntersecting);
      },
      { rootMargin: '-8% 0px -8% 0px' }
    );
    cleanups.push(() => live.disconnect());

    for (const [selector, scene] of SCENES) {
      for (const host of doc.querySelectorAll(selector)) {
        if (host.querySelector(':scope > .sku-aura') || !positioned(host)) continue;
        host.classList.add('sku-host');
        added.add({ remove: () => host.classList.remove('sku-host') });

        const aura = make('span', 'sku-aura', host);
        aura.dataset.scene = scene;
        make('i', 'sku-aura__b sku-aura__b--a', aura);
        make('i', 'sku-aura__b sku-aura__b--b', aura);

        const random = seeded(scene);
        for (let i = 0; i < 7; i += 1) {
          const spark = make('i', 'sku-aura__t', aura);
          spark.style.cssText =
            `--x:${(6 + random() * 88).toFixed(1)}%;--y:${(6 + random() * 82).toFixed(1)}%;` +
            `--s:${(i % 4 === 3 ? 12 + random() * 6 : 4 + random() * 5).toFixed(1)}px;--d:${(2.6 + random() * 3).toFixed(2)}s;` +
            `--dl:${(-random() * 6).toFixed(2)}s`;
        }
        live.observe(aura);
      }
    }
  };

  /* --- 2. Ribbon unwrap ------------------------------------------------- */
  // Photo frames only: on a phone the gift card is a tall column of text and buttons, which a ribbon must never cover.
  const WRAP_HOSTS = ['.sc-gift__frame', '.sc-offer__media', '.sc-look__frame', '.sc-night__frame'];

  const initWraps = () => {
    if (!('IntersectionObserver' in window)) return;

    const waiting = new Set();
    let burst = 0;
    let burstTimer = 0;

    const open = (host) => {
      if (!waiting.delete(host)) return;
      io.unobserve(host);
      const wrap = host.querySelector(':scope > .sku-wrap');
      if (!wrap) return;

      // Cards that appear together open one after the other.
      const delay = 380 + burst * 170;
      burst += 1;
      window.clearTimeout(burstTimer);
      burstTimer = window.setTimeout(() => {
        burst = 0;
      }, 600);

      after(delay, () => {
        wrap.classList.add('is-open');
        const remove = () => {
          wrap.remove();
          added.delete(wrap);
        };
        // The horizontal band is the last piece to finish.
        wrap.addEventListener('animationend', (event) => {
          if (event.target.classList.contains('sku-wrap__h')) remove();
        });
        after(2400, remove);
      });
    };

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!waiting.has(entry.target) || !entry.isIntersecting) continue;
          // Tall hosts can never reach 40 % of their own height on a small screen: count the screen too.
          const enough = entry.intersectionRatio >= 0.4 || entry.intersectionRect.height >= window.innerHeight * 0.38;
          if (enough) open(entry.target);
        }
      },
      { threshold: [0, 0.2, 0.4, 0.6, 0.8], rootMargin: '0px 0px -6% 0px' }
    );
    cleanups.push(() => io.disconnect());

    for (const selector of WRAP_HOSTS) {
      for (const host of doc.querySelectorAll(selector)) {
        if (host.querySelector(':scope > .sku-wrap') || !positioned(host)) continue;
        const wrap = make('span', 'sku-wrap', host);
        make('i', 'sku-wrap__v', wrap);
        make('i', 'sku-wrap__h', wrap);
        make('i', 'sku-wrap__flash', wrap);
        make('i', 'sku-wrap__bow', wrap);
        waiting.add(host);
        io.observe(host);
      }
    }

    // Safety net: never leave a ribbon on something that has long been on screen.
    const sweep = window.setInterval(() => {
      if (!waiting.size) {
        window.clearInterval(sweep);
        return;
      }
      const height = window.innerHeight;
      for (const host of Array.from(waiting)) {
        const rect = host.getBoundingClientRect();
        const seen = Math.min(rect.bottom, height) - Math.max(rect.top, 0);
        if (seen > Math.min(rect.height * 0.3, height * 0.3)) open(host);
      }
    }, 2500);
    cleanups.push(() => window.clearInterval(sweep));
  };

  /* --- 3. Hero gift box ------------------------------------------------- */
  const initGift = () => {
    const svg = doc.querySelector('.sc-hero__gift');
    if (!svg || !svg.parentElement || !('IntersectionObserver' in window) || svg.querySelector('.sku-lid')) return;

    // Expected drawing: body, lid, ribbon, bow loops, knot (assets/ snippets/sk-deco.liquid). Anything else: leave it alone.
    const parts = Array.from(svg.children);
    if (parts.length !== 5 || parts.some((part, i) => part.tagName.toLowerCase() !== (i < 3 ? 'rect' : 'path'))) return;
    const [body, lid, ribbon, loops, knot] = parts;

    // The ribbon runs over lid and body; split it so only the part on the lid travels with the lid.
    const lidRibbon = ribbon.cloneNode(false);
    ribbon.setAttribute('y', '59');
    ribbon.setAttribute('height', '59');
    lidRibbon.setAttribute('height', '24');

    const glow = doc.createElementNS(SVGNS, 'ellipse');
    glow.setAttribute('class', 'sku-glow');
    glow.setAttribute('cx', '60');
    glow.setAttribute('cy', '52');
    glow.setAttribute('rx', '34');
    glow.setAttribute('ry', '8');
    body.after(glow);

    const group = doc.createElementNS(SVGNS, 'g');
    group.setAttribute('class', 'sku-lid');
    group.append(lid, lidRibbon, loops, knot);
    svg.append(group);

    const fx = make('span', 'sku-gift-fx', svg.parentElement);
    for (let i = 0; i < 5; i += 1) make('i', 'sku-gift-fx__s', fx).style.setProperty('--i', String(i));

    let visible = false;
    let introScheduled = false;
    let sawHop = false;
    let hops = 0;
    let closeTimer = 0;

    const open = () => {
      if (!visible || svg.classList.contains('is-open')) return;
      svg.classList.add('is-open');
      fx.classList.add('is-open');
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => {
        svg.classList.remove('is-open');
        fx.classList.remove('is-open');
      }, 2600);
    };
    cleanups.push(() => {
      window.clearTimeout(closeTimer);
      svg.classList.remove('is-open');
    });

    // The festive stylesheet makes the box hop every 6 s; the lid opens after every second hop.
    const onIteration = (event) => {
      if (event.animationName !== 'skf-hop') return;
      sawHop = true;
      hops += 1;
      if (hops % 2 === 0) open();
    };
    svg.addEventListener('animationiteration', onIteration);
    cleanups.push(() => svg.removeEventListener('animationiteration', onIteration));

    const io = new IntersectionObserver(
      (entries) => {
        visible = entries.some((entry) => entry.isIntersecting);
        if (!visible || introScheduled) return;
        introScheduled = true;
        after(1300, open);
        // Without the festive hop there is no beat to follow: open on a slow timer instead.
        after(9000, () => {
          if (sawHop) return;
          const id = window.setInterval(open, 12000);
          cleanups.push(() => window.clearInterval(id));
        });
      },
      { threshold: 0.3 }
    );
    io.observe(svg);
    cleanups.push(() => io.disconnect());
  };

  /* --- Lifecycle -------------------------------------------------------- */
  const start = () => {
    if (running || designMode || reduce.matches) return;
    running = true;
    root.classList.add('sku-on');
    root.dataset.sku = 'on';
    try {
      initAura();
      initWraps();
      initGift();
    } catch (error) {
      // A decoration must never get in the way of the page.
      stop();
    }
  };

  const stop = () => {
    running = false;
    root.classList.remove('sku-on');
    delete root.dataset.sku;
    for (const fn of cleanups.splice(0)) fn();
    for (const node of added) node.remove();
    added.clear();
  };

  const boot = () => {
    start();
    reduce.addEventListener?.('change', () => (reduce.matches ? stop() : start()));
  };

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
